import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Upload,
  Code,
  User,
  Copy,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  UserIcon,
  CheckCircle,
  AlertCircle,
  Download,
  SquareMinus,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  selectSelectedSegment,
  selectSegmentsLoading,
  selectSegmentsSuccess,
  selectContacts,
  selectContactsLoading,
  selectContactsPagination,
  selectSegmentsExportBlob,
  selectSegmentsExportBlobLoading,
  selectRemoveContactSuccess,
} from 'store/segments/segments.selector';
import {
  clearSegmentState,
  getSegmentRequest,
  uploadSegmentContactsRequest,
  updateSegmentRequest,
  getSegmentContactsRequest,
  exportSegmentContactsRequest,
  clearExportSegmentContactsSuccess,
  clearRemoveContactFromSegmentSuccess,
} from 'store/segments/segments.action';
import Badge from 'components/ui/Badge';
import Button from 'components/ui/Button';
import { formatDate } from 'utils/helper';
import SegmentDeleteModal from 'components/segments/SegmentDeleteModal';
import CreateManualSegmentModal from 'components/segments/CreateManualSegmentModal';
import RemoveContactModal from 'components/segments/RemoveContactModal';
import { useToast } from 'contexts/ToastContext';
import { GetSegmentContactsRequestPayload } from 'types';
import { ContactTableSkeleton } from 'skeletons';

const ManualSegmentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const segment = useSelector(selectSelectedSegment);
  const loading = useSelector(selectSegmentsLoading);
  const contacts = useSelector(selectContacts);
  const contactsLoading = useSelector(selectContactsLoading);
  const contactsPagination = useSelector(selectContactsPagination);
  const exportBlob = useSelector(selectSegmentsExportBlob);
  const exportBlobLoading = useSelector(selectSegmentsExportBlobLoading);

  const [activeTab, setActiveTab] = useState<
    'overview' | 'people' | 'usage' | 'ad-audiences'
  >('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [showRemoveContactModal, setShowRemoveContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [csvStep, setCsvStep] = useState<1 | 2 | 3>(1);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [hasHeaders, setHasHeaders] = useState(true);
  const [lookupBy, setLookupBy] = useState<'custom_id' | 'email'>('custom_id');
  const [fieldMapping, setFieldMapping] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    custom_id?: string;
    unsubscribed?: string;
  }>({});
  const success = useSelector(selectSegmentsSuccess);
  const removeContactSuccess = useSelector(selectRemoveContactSuccess);

  const getSegmentContacts = useCallback(
    (page: number = 0, limit?: number) => {
      if (segment && id && segment.contacts.length > 0) {
        const payload: GetSegmentContactsRequestPayload = {
          id,
          page,
          limit: limit ?? contactsPagination.limit,
        };
        dispatch(getSegmentContactsRequest(payload));
      }
    },
    [segment, id, contactsPagination.limit, dispatch]
  );

  useEffect(() => {
    if (removeContactSuccess) {
      if (segment?._id) {
        dispatch(getSegmentRequest(segment._id));
        getSegmentContacts(0);
      }
      setShowRemoveContactModal(false);
      setSelectedContact(null);
      dispatch(clearRemoveContactFromSegmentSuccess());
    }
  }, [removeContactSuccess, dispatch, segment?._id, getSegmentContacts]);

  useEffect(() => {
    if (id) {
      dispatch(getSegmentRequest(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (segment && id && segment.contacts.length > 0) {
      getSegmentContacts(0);
    }
  }, [segment, id, getSegmentContacts]);

  const handlePageChange = (page: number) => {
    if (segment && id && segment.contacts.length > 0) {
      getSegmentContacts(page);
    }
  };

  const handlePageSizeChange = (newLimit: number) => {
    if (segment && id && segment.contacts.length > 0) {
      getSegmentContacts(0, newLimit);
    }
  };

  const handleExportContacts = () => {
    if (id) {
      dispatch(exportSegmentContactsRequest(id));
    }
  };

  // Calculate proper pagination values
  const totalContacts = segment?.contacts?.length || 0;
  const currentPage = contactsPagination.page;
  const limit = contactsPagination.limit;
  const totalPages = Math.ceil(totalContacts / limit);
  const startItem = currentPage * limit + 1;
  const endItem = Math.min((currentPage + 1) * limit, totalContacts);

  useEffect(() => {
    if (id && success) {
      setShowEditModal(false);
      dispatch(getSegmentRequest(id));
      showToast({
        type: 'success',
        title: 'Segment saved',
        message: 'Segment has been saved successfully',
      });
      dispatch(clearSegmentState());
    }
  }, [success, dispatch, id, showToast]);

  // Handle export success
  useEffect(() => {
    if (exportBlob && id) {
      const url = window.URL.createObjectURL(exportBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `segment_${segment?.name || id}_contacts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({
        type: 'success',
        title: 'Export Successful',
        message: 'Segment contacts have been exported successfully.',
      });

      dispatch(clearExportSegmentContactsSuccess());
    }
  }, [exportBlob, id, segment?.name, showToast, dispatch]);

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleCSVUpload = () => {
    setShowCSVUpload(true);
    setCsvStep(1);
  };

  // Auto-detect field mappings based on common column names
  const autoDetectFields = (headers: string[]) => {
    const mapping: any = {};

    headers.forEach((header) => {
      const lowerHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');

      // Email detection
      if (
        !mapping.email &&
        (lowerHeader.includes('email') ||
          lowerHeader.includes('e-mail') ||
          lowerHeader.includes('mail'))
      ) {
        mapping.email = header;
      }

      // First name detection
      if (
        !mapping.firstName &&
        (lowerHeader.includes('firstname') ||
          lowerHeader.includes('first_name') ||
          lowerHeader.includes('first') ||
          lowerHeader.includes('fname') ||
          lowerHeader.includes('givenname') ||
          lowerHeader.includes('given_name'))
      ) {
        mapping.firstName = header;
      }

      // Last name detection
      if (
        !mapping.lastName &&
        (lowerHeader.includes('lastname') ||
          lowerHeader.includes('last_name') ||
          lowerHeader.includes('last') ||
          lowerHeader.includes('lname') ||
          lowerHeader.includes('surname') ||
          lowerHeader.includes('familyname') ||
          lowerHeader.includes('family_name'))
      ) {
        mapping.lastName = header;
      }

      // Custom ID detection
      if (
        !mapping.custom_id &&
        (lowerHeader.includes('custom id') ||
          lowerHeader.includes('customid') ||
          lowerHeader.includes('custom_id'))
      ) {
        mapping.custom_id = header;
      }

      if (
        !mapping.unsubscribed &&
        (lowerHeader.includes('unsubscribed') ||
          lowerHeader.includes('unsub') ||
          lowerHeader.includes('unsubscribed'))
      ) {
        mapping.unsubscribed = header;
      }
    });

    return mapping;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = [
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
        'application/vnd.ms-excel', // xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.template', // xltx
        'application/vnd.ms-excel.template.macroEnabled.12', // xltm
        'application/vnd.ms-excel.sheet.macroEnabled.12', // xlsm
        'application/vnd.ms-excel.sheet.binary.macroEnabled.12', // xlsb
        'application/vnd.oasis.opendocument.text', // odt
        'application/vnd.oasis.opendocument.spreadsheet', // ods
      ];

      const validExtensions = [
        '.csv',
        '.xlsx',
        '.xls',
        '.xltx',
        '.xltm',
        '.xlsm',
        '.xlsb',
        '.odt',
        '.ods',
      ];
      const hasValidExtension = validExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );

      if (validTypes.includes(file.type) || hasValidExtension) {
        setUploadedFile(file);

        // Parse file based on type
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const result = e.target?.result;

            if (
              file.type === 'text/csv' ||
              file.name.toLowerCase().endsWith('.csv')
            ) {
              // Handle CSV files
              const text = result as string;
              const lines = text.split('\n').filter((line) => line.trim());

              if (lines.length > 0) {
                const headers = lines[0]
                  .split(',')
                  .map((h) => h.trim().replace(/"/g, ''));
                setCsvHeaders(headers);

                const data = lines.slice(hasHeaders ? 1 : 0).map((line) => {
                  const values = line
                    .split(',')
                    .map((v) => v.trim().replace(/"/g, ''));
                  const row: any = {};
                  headers.forEach((header, index) => {
                    row[header] = values[index] || '';
                  });
                  return row;
                });
                setCsvData(data);
                // Auto-detect field mappings
                const detectedMapping = autoDetectFields(headers);
                setFieldMapping(detectedMapping);
              }
            } else {
              // Handle Excel and other spreadsheet files
              if (typeof XLSX !== 'undefined') {
                const workbook = XLSX.read(result, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];

                // Convert to JSON with headers
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                  header: 1,
                });

                if (jsonData.length > 0) {
                  const headers = jsonData[0] as string[];
                  setCsvHeaders(headers);

                  const data = jsonData
                    .slice(hasHeaders ? 1 : 0)
                    .map((row: any) => {
                      const rowData: any = {};
                      headers.forEach((header, index) => {
                        rowData[header] = row[index] || '';
                      });
                      return rowData;
                    });
                  setCsvData(data);
                  // Auto-detect field mappings
                  const detectedMapping = autoDetectFields(headers);
                  setFieldMapping(detectedMapping);
                }
              } else {
                // Fallback for when XLSX library is not loaded
                showToast({
                  type: 'error',
                  title: 'Library not loaded',
                  message: 'Please refresh the page and try again.',
                });
              }
            }
          } catch (error) {
            console.error('Error parsing file:', error);
            showToast({
              type: 'error',
              title: 'File parsing error',
              message:
                'Unable to parse the uploaded file. Please check the file format.',
            });
          }
        };

        if (
          file.type === 'text/csv' ||
          file.name.toLowerCase().endsWith('.csv')
        ) {
          reader.readAsText(file);
        } else {
          reader.readAsBinaryString(file);
        }
      } else {
        showToast({
          type: 'error',
          title: 'Invalid file type',
          message: 'Please upload a CSV, XLSX, or ODT file.',
        });
      }
    }
  };

  const isFieldMappingComplete = () => {
    if (lookupBy === 'custom_id') {
      return !!fieldMapping.custom_id;
    }
    return !!fieldMapping.email;
  };

  // Validate and analyze the uploaded data
  const validateData = () => {
    const results = {
      valid: [] as any[],
      warnings: [] as any[],
      errors: [] as any[],
      totalProcessed: csvData.length,
    };

    csvData.forEach((row, index) => {
      const rowNumber = index + 1;
      const email = fieldMapping.email ? row[fieldMapping.email] : '';
      const firstName = fieldMapping.firstName
        ? row[fieldMapping.firstName]
        : '';
      const lastName = fieldMapping.lastName ? row[fieldMapping.lastName] : '';
      const custom_id = fieldMapping.custom_id
        ? row[fieldMapping.custom_id]
        : '';

      const unsubscribed = fieldMapping.unsubscribed
        ? row[fieldMapping.unsubscribed]?.toString().toLowerCase() === 'true'
          ? true
          : false
        : false;

      const hasRequiredLookupValue =
        lookupBy === 'custom_id'
          ? !!custom_id?.toString().trim()
          : !!email?.toString().trim();

      // Extract attributes from unmapped columns
      const attributes: { [key: string]: any } = {};
      const mappedColumns = Object.values(fieldMapping).filter(Boolean);

      // Add all unmapped columns as attributes
      Object.keys(row).forEach((columnName) => {
        if (
          !mappedColumns.includes(columnName) &&
          row[columnName] !== undefined &&
          row[columnName] !== ''
        ) {
          attributes[columnName] = row[columnName];
        }
      });

      const rowData = {
        email,
        firstName,
        lastName,
        custom_id,
        unsubscribed,
        attributes,
      };

      if (!hasRequiredLookupValue) {
        results.errors.push({
          row: rowNumber,
          message:
            lookupBy === 'custom_id'
              ? 'Missing required custom id'
              : 'Missing required email',
          data: rowData,
        });
      } else {
        results.valid.push({
          row: rowNumber,
          data: rowData,
        });
      }
    });

    return results;
  };

  const handleNextStep = () => {
    if (csvStep < 3) {
      setCsvStep((prev) => (prev + 1) as 1 | 2 | 3);
    }
  };

  const handlePrevStep = () => {
    if (csvStep > 1) {
      setCsvStep((prev) => (prev - 1) as 1 | 2 | 3);
    }
  };

  const handleCancel = () => {
    setShowCSVUpload(false);
    setCsvStep(1);
    setUploadedFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({});
  };

  const handleAddPeople = () => {
    // Get validation results and only process valid data
    const validationResults = validateData();
    const validData = validationResults.valid.map((item) => item.data);

    if (id && validData.length > 0) {
      dispatch(uploadSegmentContactsRequest(id, validData, lookupBy));
    }

    if (validData.length > 0) {
      showToast({
        type: 'success',
        title: 'Data ready for import',
        message: `${validData.length} people will be added to the segment.`,
      });
    } else {
      showToast({
        type: 'error',
        title: 'No valid data',
        message: 'Please fix the errors before importing.',
      });
      return;
    }

    // Reset the flow
    handleCancel();
  };

  if (loading) {
    return (
      <div className="p-8 bg-background min-h-screen">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-4" />
          <div className="h-6 bg-muted rounded w-1/2 mb-8" />
          <div className="h-96 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  if (!segment) {
    return (
      <div className="p-8 bg-background min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Segment not found
          </h2>
          <p className="text-muted-foreground mb-4">
            The segment you're looking for doesn't exist.
          </p>
          <Button
            variant="ghost"
            size="medium"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Segments
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'people', label: 'People', count: segment?.contacts?.length || 0 },
  ];

  return (
    <div className="p-6">
      {/* Segment Title and Info */}
      <div className="flex justify-between">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-semibold text-foreground">
              {segment.name}
            </h1>
          </div>
          <p className="text-muted-foreground text-sm mb-3">
            {segment.description}
          </p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              ID:{' '}
              <span className="text-destructive font-mono text-xs">
                {segment._id}
              </span>
            </span>
            <Copy className="w-4 h-4 cursor-pointer hover:text-foreground transition-colors" />
            <span>Last saved {formatDate(segment.updatedAt)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleEdit} variant="outline" size="small">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button
            onClick={handleDelete}
            variant="outline"
            size="small"
            className="text-destructive hover:text-destructive-hover hover:border-destructive"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="w-full mb-6">
        <div className="inline-flex items-center gap-1 rounded-lg bg-muted/80 px-1.5 py-1 border border-border/50">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer
                      transition-all duration-200 cursor-pointer
                      ${
                        activeTab === tab.id
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                      }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={`py-0.5 px-2 rounded-full text-xs font-medium flex items-center justify-center ${
                    activeTab === tab.id
                      ? 'bg-primary/80 text-primary-foreground'
                      : 'bg-muted-foreground/20 text-muted-foreground'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex gap-8">
        <div className="flex-1">
          {activeTab === 'overview' && !showCSVUpload && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-6">
                Add people to this segment
              </h3>

              {/* Add People Options */}
              <div className="grid gap-4 sm:grid-cols-3">
                {/* Add by CSV — primary action */}
                <button
                  type="button"
                  onClick={handleCSVUpload}
                  className="group relative flex flex-col gap-3 rounded-xl border border-border bg-card p-5 text-left shadow-sm transition-all duration-200 hover:border-primary/30 hover:bg-primary/5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary transition-colors group-hover:bg-primary/25">
                      <Upload className="h-5 w-5" />
                    </div>
                    <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary group-hover:bg-primary/20">
                      Get started
                    </span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">
                      Add by CSV
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      People already in your workspace can be added by id or
                      email address.
                    </p>
                  </div>
                  <ChevronRight className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </button>

                {/* Add by API */}
                <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary/15 text-secondary">
                    <Code className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">
                      Add by API
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Add or remove people by id through our API.
                    </p>
                  </div>
                </div>

                {/* Add manually in the UI */}
                <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/20 text-accent-foreground">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">
                      Add manually in the UI
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Add or remove people from any person's Segments tab.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'overview' && showCSVUpload && (
            <div>
              {/* Step Indicator */}
              <div className="mb-8">
                <div className="flex items-center space-x-4">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div
                        className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                          csvStep >= step
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-border text-muted-foreground'
                        }`}
                      >
                        {csvStep > step ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <span>{step}</span>
                        )}
                      </div>
                      <div className="ml-2">
                        <div
                          className={`text-sm font-medium ${
                            csvStep >= step
                              ? 'text-primary'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {step === 1 && 'Upload File'}
                          {step === 2 && 'Map'}
                          {step === 3 && 'Review'}
                        </div>
                      </div>
                      {step < 3 && (
                        <div
                          className={`w-12 h-0.5 ml-4 ${
                            csvStep > step ? 'bg-primary' : 'bg-border'
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              {csvStep === 1 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-6">
                    Upload File
                  </h3>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <p className="text-sm text-muted-foreground mb-4">
                      Adding to a manual segment requires an email or id. See an
                      example file.
                    </p>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        How do you want to look up existing people?
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center text-foreground cursor-pointer">
                          <input
                            type="radio"
                            name="lookupBy"
                            value="custom_id"
                            checked={lookupBy === 'custom_id'}
                            onChange={(e) =>
                              setLookupBy(
                                e.target.value as 'custom_id' | 'email'
                              )
                            }
                            className="mr-2 accent-primary border-border focus:ring-0"
                          />
                          By id
                        </label>
                        <label className="flex items-center text-foreground cursor-pointer">
                          <input
                            type="radio"
                            name="lookupBy"
                            value="email"
                            checked={lookupBy === 'email'}
                            onChange={(e) =>
                              setLookupBy(
                                e.target.value as 'custom_id' | 'email'
                              )
                            }
                            className="mr-2 accent-primary border-border focus:ring-0"
                          />
                          By email
                        </label>
                      </div>
                    </div>
                    {/* Required Fields Table */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-foreground mb-3">
                        Required Fields
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-border rounded-lg">
                          <thead className="bg-muted">
                            <tr>
                              <th className="p-3 text-left border-b border-border font-medium text-foreground">
                                Field
                              </th>
                              <th className="p-3 text-left border-b border-border font-medium text-foreground">
                                Description
                              </th>
                              <th className="p-3 text-left border-b border-border font-medium text-foreground">
                                Required
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="p-3 border-b border-border font-medium text-foreground">
                                Email
                              </td>
                              <td className="p-3 border-b border-border text-muted-foreground">
                                Email address of the person
                              </td>
                              <td className="p-3 border-b border-border">
                                <span className="text-muted-foreground font-medium">
                                  {lookupBy === 'email'
                                    ? 'Required'
                                    : 'Optional'}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className="p-3 border-b border-border font-medium text-foreground">
                                First Name
                              </td>
                              <td className="p-3 border-b border-border text-muted-foreground">
                                First name of the person
                              </td>
                              <td className="p-3 border-b border-border">
                                <span className="text-muted-foreground font-medium">
                                  Optional
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className="p-3 border-b border-border font-medium text-foreground">
                                Last Name
                              </td>
                              <td className="p-3 border-b border-border text-muted-foreground">
                                Last name of the person
                              </td>
                              <td className="p-3 border-b border-border">
                                <span className="text-muted-foreground font-medium">
                                  Optional
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className="p-3 font-medium text-foreground">
                                Custom ID
                              </td>
                              <td className="p-3 text-muted-foreground">
                                Custom identifier for the person
                              </td>
                              <td className="p-3">
                                <span className="text-muted-foreground font-medium">
                                  {lookupBy === 'custom_id'
                                    ? 'Required'
                                    : 'Optional'}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="border-2 border-dashed border-border rounded-lg p-8 text-center bg-muted/30">
                      <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <div className="mb-4">
                        <label htmlFor="csvFile" className="cursor-pointer">
                          <span className="text-primary hover:text-primary-hover font-medium">
                            Choose a file to upload
                          </span>
                          <input
                            id="csvFile"
                            type="file"
                            accept=".csv,.xlsx,.xls,.xltx,.xltm,.xlsm,.xlsb,.odt,.ods"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Supported formats: CSV, XLSX, ODT, ODS
                      </p>
                      {uploadedFile && (
                        <div className="flex items-center justify-center gap-2 text-sm text-success">
                          <CheckCircle className="w-4 h-4" />
                          <span>{uploadedFile.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {csvStep === 2 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-6">
                    Map
                  </h3>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <p className="text-sm text-muted-foreground mb-4">
                      Does your CSV have headers?
                    </p>
                    <div className="flex gap-4 mb-6">
                      <label className="flex items-center text-foreground cursor-pointer">
                        <input
                          type="radio"
                          name="hasHeaders"
                          checked={hasHeaders}
                          onChange={() => setHasHeaders(true)}
                          className="mr-2 accent-primary border-border focus:ring-0"
                        />
                        Yes
                      </label>
                      <label className="flex items-center text-foreground cursor-pointer">
                        <input
                          type="radio"
                          name="hasHeaders"
                          checked={!hasHeaders}
                          onChange={() => setHasHeaders(false)}
                          className="mr-2 accent-primary border-border focus:ring-0"
                        />
                        No
                      </label>
                    </div>

                    <p className="text-sm font-medium text-foreground mb-4">
                      Which column from your CSV maps to id?
                    </p>
                    <p className="text-xs text-muted-foreground mb-4">
                      Note: Any unmapped columns will be automatically saved as
                      contact attributes.
                    </p>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          First Name
                        </label>
                        <select
                          value={fieldMapping.firstName || ''}
                          onChange={(e) =>
                            setFieldMapping((prev) => ({
                              ...prev,
                              firstName: e.target.value,
                            }))
                          }
                          className="w-full p-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                        >
                          <option value="" disabled>
                            Select column
                          </option>
                          {csvHeaders.map((header) => {
                            const isSelected =
                              Object.values(fieldMapping).includes(header) &&
                              fieldMapping.firstName !== header;
                            return (
                              <option
                                key={header}
                                value={header}
                                disabled={isSelected}
                                className={
                                  isSelected ? 'text-muted-foreground' : ''
                                }
                              >
                                {header} {isSelected ? '(already mapped)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Last Name
                        </label>
                        <select
                          value={fieldMapping.lastName || ''}
                          onChange={(e) =>
                            setFieldMapping((prev) => ({
                              ...prev,
                              lastName: e.target.value,
                            }))
                          }
                          className="w-full p-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                        >
                          <option value="" disabled>
                            Select column
                          </option>
                          {csvHeaders.map((header) => {
                            const isSelected =
                              Object.values(fieldMapping).includes(header) &&
                              fieldMapping.lastName !== header;
                            return (
                              <option
                                key={header}
                                value={header}
                                disabled={isSelected}
                                className={
                                  isSelected ? 'text-muted-foreground' : ''
                                }
                              >
                                {header} {isSelected ? '(already mapped)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Email
                          {lookupBy === 'email' && (
                            <span className="text-destructive"> *</span>
                          )}
                        </label>
                        <select
                          value={fieldMapping.email || ''}
                          onChange={(e) =>
                            setFieldMapping((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          className="w-full p-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                        >
                          <option value="" disabled>
                            Select column
                          </option>
                          {csvHeaders.map((header) => {
                            const isSelected =
                              Object.values(fieldMapping).includes(header) &&
                              fieldMapping.email !== header;
                            return (
                              <option
                                key={header}
                                value={header}
                                disabled={isSelected}
                                className={
                                  isSelected ? 'text-muted-foreground' : ''
                                }
                              >
                                {header} {isSelected ? '(already mapped)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Custom ID
                          {lookupBy === 'custom_id' && (
                            <span className="text-destructive"> *</span>
                          )}
                        </label>
                        <select
                          value={fieldMapping.custom_id || ''}
                          onChange={(e) =>
                            setFieldMapping((prev) => ({
                              ...prev,
                              custom_id: e.target.value,
                            }))
                          }
                          className="w-full p-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                        >
                          <option value="" disabled>
                            Select column
                          </option>
                          {csvHeaders.map((header) => {
                            const isSelected =
                              Object.values(fieldMapping).includes(header) &&
                              fieldMapping.custom_id !== header;
                            return (
                              <option
                                key={header}
                                value={header}
                                disabled={isSelected}
                                className={
                                  isSelected ? 'text-muted-foreground' : ''
                                }
                              >
                                {header} {isSelected ? '(already mapped)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    {/* Field Mapping Status */}
                    {csvStep === 2 && (
                      <div className="mt-4 p-3 rounded-lg border border-border bg-muted/30">
                        {isFieldMappingComplete() ? (
                          <div className="flex items-center gap-2 text-success">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              All required fields are mapped
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-warning">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">
                              Please map all required fields to continue
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {csvData.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-medium text-foreground mb-3">
                          Preview
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-border rounded-lg">
                            <thead className="bg-muted">
                              <tr>
                                {Object.entries(fieldMapping).map(
                                  ([field, column]) =>
                                    column && (
                                      <th
                                        key={field}
                                        className="p-2 text-left border-b border-border text-foreground"
                                      >
                                        {field}
                                      </th>
                                    )
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {csvData.slice(0, 2).map((row, index) => (
                                <tr key={index}>
                                  {Object.entries(fieldMapping).map(
                                    ([field, column]) =>
                                      column && (
                                        <td
                                          key={field}
                                          className="p-2 border-b border-border text-muted-foreground"
                                        >
                                          {column === 'unsubscribed'
                                            ? row[column]
                                                ?.toString()
                                                .toLowerCase() === 'true'
                                              ? 'true'
                                              : 'false'
                                            : row[column]?.toString() || '-'}
                                        </td>
                                      )
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {csvStep === 3 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-6">
                    Review
                  </h3>
                  <div className="bg-card border border-border rounded-lg p-6">
                    {(() => {
                      const validationResults = validateData();
                      const { valid, warnings, errors, totalProcessed } =
                        validationResults;

                      return (
                        <>
                          {/* Status Alert */}
                          {errors.length === totalProcessed ? (
                            <div className="bg-destructive-background border border-destructive/30 rounded-lg p-4 mb-6">
                              <div className="flex items-center gap-2 text-destructive mb-2">
                                <AlertCircle className="w-5 h-5" />
                                <span className="font-medium">
                                  No valid data found to import.
                                </span>
                              </div>
                              <p className="text-sm text-destructive">
                                We processed {totalProcessed} rows and found{' '}
                                {errors.length} errors. Please fix the issues
                                and try again.
                              </p>
                            </div>
                          ) : errors.length > 0 ? (
                            <div className="bg-warning-background border border-warning/30 rounded-lg p-4 mb-6">
                              <div className="flex items-center gap-2 text-warning mb-2">
                                <AlertCircle className="w-5 h-5" />
                                <span className="font-medium">
                                  Some issues found with your data.
                                </span>
                              </div>
                              <p className="text-sm text-warning-foreground">
                                We found {valid.length} valid records,{' '}
                                {warnings.length} warnings, and {errors.length}{' '}
                                errors.
                              </p>
                            </div>
                          ) : (
                            <div className="bg-success-background border border-success/30 rounded-lg p-4 mb-6">
                              <div className="flex items-center gap-2 text-success mb-2">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-medium">
                                  All data looks good!
                                </span>
                              </div>
                              <p className="text-sm text-success">
                                We found {valid.length} valid records ready to
                                import.
                                {warnings.length > 0 &&
                                  ` ${warnings.length} warnings found.`}
                              </p>
                            </div>
                          )}

                          {/* Statistics Grid */}
                          <div className="grid grid-cols-3 gap-6 text-center">
                            <div>
                              <div
                                className={`text-2xl font-bold ${valid.length > 0 ? 'text-success' : 'text-foreground'}`}
                              >
                                {valid.length}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                PEOPLE FOUND
                              </div>
                              <button className="text-primary text-sm hover:underline mt-1">
                                How was this calculated?
                              </button>
                            </div>
                            <div>
                              <div
                                className={`text-2xl font-bold ${warnings.length > 0 ? 'text-warning' : 'text-foreground'}`}
                              >
                                {warnings.length}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                WARNINGS
                              </div>
                            </div>
                            <div>
                              <div
                                className={`text-2xl font-bold ${errors.length > 0 ? 'text-destructive' : 'text-foreground'}`}
                              >
                                {errors.length}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                ERRORS
                              </div>
                            </div>
                          </div>

                          {/* Attributes Summary */}
                          {valid.length > 0 &&
                            (() => {
                              const allAttributes = new Set<string>();
                              valid.forEach((item) => {
                                if (item.data.attributes) {
                                  Object.keys(item.data.attributes).forEach(
                                    (attr) => allAttributes.add(attr)
                                  );
                                }
                              });

                              if (allAttributes.size > 0) {
                                return (
                                  <div className="mt-6 p-4 bg-info-background border border-info/30 rounded-lg">
                                    <h4 className="font-medium text-foreground mb-2">
                                      Additional Attributes Detected
                                    </h4>
                                    <p className="text-sm text-muted-foreground mb-2">
                                      The following {allAttributes.size}{' '}
                                      unmapped column
                                      {allAttributes.size > 1 ? 's' : ''} will
                                      be saved as contact attributes:
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {Array.from(allAttributes).map((attr) => (
                                        <span
                                          key={attr}
                                          className="px-2 py-1 bg-info/20 text-foreground text-xs rounded-md"
                                        >
                                          {attr}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}

                          {/* Error Details */}
                          {errors.length > 0 && (
                            <div className="mt-6">
                              <h4 className="font-medium text-foreground mb-2">
                                {errors.length} Errors - Some rows will not be
                                added to the segment
                              </h4>
                              <p className="text-sm text-muted-foreground mb-4">
                                The following rows have issues that need to be
                                fixed:
                              </p>

                              <div className="max-h-60 overflow-y-auto border border-border rounded-lg">
                                <table className="w-full text-sm">
                                  <thead className="bg-muted sticky top-0">
                                    <tr>
                                      <th className="p-2 text-left border-b border-border text-foreground">
                                        Row
                                      </th>
                                      <th className="p-2 text-left border-b border-border text-foreground">
                                        Issue
                                      </th>
                                      <th className="p-2 text-left border-b border-border text-foreground">
                                        Data
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {errors.slice(0, 10).map((error, index) => (
                                      <tr
                                        key={index}
                                        className="border-b border-border"
                                      >
                                        <td className="p-2 font-medium text-foreground">
                                          {error.row}
                                        </td>
                                        <td className="p-2 text-destructive">
                                          {error.message}
                                        </td>
                                        <td className="p-2 text-muted-foreground">
                                          {error.data.email || 'N/A'} |{' '}
                                          {error.data.firstName || 'N/A'} |{' '}
                                          {error.data.lastName || 'N/A'} |{' '}
                                          {error.data.custom_id || 'N/A'}
                                        </td>
                                      </tr>
                                    ))}
                                    {errors.length > 10 && (
                                      <tr>
                                        <td
                                          colSpan={3}
                                          className="p-2 text-center text-muted-foreground"
                                        >
                                          ... and {errors.length - 10} more
                                          errors
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              <div className="flex gap-2 mt-4">
                                <Button variant="outline" size="small">
                                  Export error file
                                </Button>
                                <Button
                                  variant="outline"
                                  size="small"
                                  onClick={handlePrevStep}
                                >
                                  Go back to upload
                                </Button>
                                <Button variant="outline" size="small">
                                  Download Original File
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Warning Details */}
                          {warnings.length > 0 && (
                            <div className="mt-6">
                              <h4 className="font-medium text-foreground mb-2">
                                {warnings.length} Warnings - Data quality issues
                              </h4>
                              <p className="text-sm text-muted-foreground mb-4">
                                These issues won't prevent import but may affect
                                data quality:
                              </p>

                              <div className="max-h-40 overflow-y-auto border border-border rounded-lg">
                                <table className="w-full text-sm">
                                  <thead className="bg-muted sticky top-0">
                                    <tr>
                                      <th className="p-2 text-left border-b border-border text-foreground">
                                        Row
                                      </th>
                                      <th className="p-2 text-left border-b border-border text-foreground">
                                        Warning
                                      </th>
                                      <th className="p-2 text-left border-b border-border text-foreground">
                                        Data
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {warnings
                                      .slice(0, 5)
                                      .map((warning, index) => (
                                        <tr
                                          key={index}
                                          className="border-b border-border"
                                        >
                                          <td className="p-2 font-medium text-foreground">
                                            {warning.row}
                                          </td>
                                          <td className="p-2 text-warning">
                                            {warning.message}
                                          </td>
                                          <td className="p-2 text-muted-foreground">
                                            {warning.data.email} |{' '}
                                            {warning.data.firstName} |{' '}
                                            {warning.data.lastName} |{' '}
                                            {warning.data.custom_id}
                                          </td>
                                        </tr>
                                      ))}
                                    {warnings.length > 5 && (
                                      <tr>
                                        <td
                                          colSpan={3}
                                          className="p-2 text-center text-muted-foreground"
                                        >
                                          ... and {warnings.length - 5} more
                                          warnings
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between mt-8">
                <div>
                  {csvStep > 1 && (
                    <Button onClick={handlePrevStep} variant="outline">
                      Back
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCancel} variant="outline">
                    Cancel
                  </Button>
                  {csvStep < 3 && (
                    <Button
                      onClick={handleNextStep}
                      variant="primary"
                      disabled={
                        (csvStep === 1 && !uploadedFile) ||
                        (csvStep === 2 && !isFieldMappingComplete())
                      }
                    >
                      Next
                    </Button>
                  )}
                  {csvStep === 3 && (
                    <Button onClick={handleAddPeople} variant="primary">
                      Add People
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'people' && (
            <div>
              {segment?.contacts?.length > 0 ? (
                <div>
                  {/* Actions Bar */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-muted-foreground">
                      {segment?.contacts?.length} people
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="small"
                        variant="success"
                        onClick={handleExportContacts}
                        disabled={
                          exportBlobLoading || !segment?.contacts?.length
                        }
                        className="flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        {exportBlobLoading ? 'Exporting...' : 'Export'}
                      </Button>
                    </div>
                  </div>

                  {/* Contacts Table */}
                  {contactsLoading ? (
                    <ContactTableSkeleton />
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-border bg-card">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border bg-muted/50">
                            <th className="px-4 py-3 text-left">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded border-border accent-primary focus:ring-0"
                              />
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                              EMAIL
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                              NAME
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                              CUSTOM ID
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                              LAST LOGIN
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                              JOIN DATE
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                              STATUS
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-destructive">
                              ACTIONS
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-card">
                          {contacts?.map((contact, index) => (
                            <tr
                              key={contact._id ?? index}
                              className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                            >
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 rounded border-border accent-primary focus:ring-0"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  className="text-sm text-primary hover:text-primary-hover cursor-pointer text-left"
                                  onClick={() =>
                                    navigate(`/contacts/${contact._id}`)
                                  }
                                >
                                  {contact.email}
                                </button>
                              </td>
                              <td className="px-4 py-3 font-medium text-foreground">
                                {contact.firstName} {contact.lastName}
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant="default">
                                  {contact.custom_id}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {formatDate(contact.updatedAt)}
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {formatDate(contact.createdAt)}
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant="active">Active</Badge>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedContact(contact);
                                    setShowRemoveContactModal(true);
                                  }}
                                  className="text-destructive hover:text-destructive-hover w-6 h-6 transition-colors"
                                  aria-label="Remove from segment"
                                >
                                  <SquareMinus className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {!contactsLoading &&
                        contacts.length > 0 &&
                        totalContacts > 0 && (
                          <div className="px-4 py-3 border-t border-border bg-muted/30">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="text-sm text-muted-foreground">
                                  {startItem}-{endItem} of {totalContacts}
                                </div>

                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">
                                    Show:
                                  </span>
                                  <div className="relative">
                                    <select
                                      value={limit}
                                      onChange={(e) =>
                                        handlePageSizeChange(
                                          Number(e.target.value)
                                        )
                                      }
                                      className="appearance-none bg-card border border-border rounded-lg px-2 py-1 pr-8 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
                                      aria-label="Items per page"
                                    >
                                      <option value={10}>10</option>
                                      <option value={25}>25</option>
                                      <option value={50}>50</option>
                                      <option value={100}>100</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                    </div>
                                  </div>
                                  <span className="text-sm text-muted-foreground">
                                    per page
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  disabled={currentPage === 0}
                                  onClick={() =>
                                    handlePageChange(currentPage - 1)
                                  }
                                  aria-label="Previous page"
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                </button>

                                <span className="text-sm text-muted-foreground px-3 py-1">
                                  Page {currentPage + 1} of {totalPages}
                                </span>

                                <button
                                  type="button"
                                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  disabled={currentPage >= totalPages - 1}
                                  onClick={() =>
                                    handlePageChange(currentPage + 1)
                                  }
                                  aria-label="Next page"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <UserIcon className="w-12 h-12 text-muted-foreground mb-4" />
                    <h3 className="text-foreground font-medium text-base mb-2">
                      No customers found
                    </h3>
                    <p className="text-muted-foreground text-sm max-w-xs">
                      No customers match the current sample criteria
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <SegmentDeleteModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
      {showEditModal && (
        <CreateManualSegmentModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={(segment) => {
            dispatch(
              updateSegmentRequest({
                id: id || '',
                data: {
                  name: segment.name?.trim() || '',
                  description: segment.description?.trim() || '',
                  type: 'Manual',
                  conditions: {},
                },
              })
            );
          }}
          initialName={segment.name}
          initialDescription={segment.description}
        />
      )}
      {showRemoveContactModal && (
        <RemoveContactModal
          isOpen={showRemoveContactModal}
          onClose={() => {
            setShowRemoveContactModal(false);
            setSelectedContact(null);
          }}
          segment={segment}
          contact={selectedContact}
        />
      )}
    </div>
  );
};

export default ManualSegmentDetails;
